import React, { useState } from "react";
import { validateIban, BANK_BRANDS } from "@/lib/iban";
import { ShieldCheck, ShieldAlert, Search, Hash, Building2 } from "lucide-react";
import { groupIban } from "@/lib/format";

export default function IbanValidator() {
  const [iban, setIban] = useState("");
  const [result, setResult] = useState(null);

  const check = (e) => {
    e.preventDefault();
    setResult(validateIban(iban));
  };

  // Live preview as user types
  React.useEffect(() => {
    if (!iban) { setResult(null); return; }
    if (iban.replace(/\s/g, "").length >= 6) {
      setResult(validateIban(iban));
    }
  }, [iban]);

  const bankColor = result?.valid ? (BANK_BRANDS[result.bank_name] || "#2563EB") : "#2563EB";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="iban-page">
      <div>
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600">Herramienta</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">Validador de IBAN</h1>
        <p className="text-slate-500 mt-1">Pega un IBAN — detectamos país, banco y validez (ISO 13616 / mod-97) al instante.</p>
      </div>

      <form onSubmit={check} className="bg-white border border-slate-200 rounded-3xl p-6 flex flex-col md:flex-row gap-3" data-testid="iban-form">
        <input
          required value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())}
          placeholder="Ej. ES91 2100 0418 4502 0005 1332"
          data-testid="iban-input"
          className="flex-1 px-5 py-4 rounded-full border border-slate-200 font-mono-fin tracking-wider focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none text-lg"
        />
        <button type="submit"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-4 rounded-full font-semibold hover:bg-slate-900 transition-colors justify-center"
                data-testid="iban-submit">
          <Search size={16} /> Validar
        </button>
      </form>

      {result && (
        <div
          className={`rounded-3xl p-6 ${result.valid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}
          data-testid="iban-result"
        >
          <div className="flex items-center gap-4">
            {result.valid ? (
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold"
                   style={{ background: bankColor }}>
                {result.bank_name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
                <ShieldAlert className="text-red-600" size={26} />
              </div>
            )}
            <div className="flex-1">
              <div className="font-display font-bold text-2xl text-slate-900 flex items-center gap-3 flex-wrap">
                <span>{result.valid ? result.bank_name : "IBAN inválido"}</span>
                {result.valid && (
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono-fin font-bold ${
                      result.network === "SEPA" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {result.network}
                  </span>
                )}
              </div>
              <div className="text-sm text-slate-600 mt-0.5">
                {result.valid ? (
                  <span className="inline-flex items-center gap-2"><ShieldCheck size={14} className="text-green-600" /> IBAN verificado · checksum válido · {result.country_flag} {result.country_name}</span>
                ) : (result.reason || "Revisa el formato")}
              </div>
            </div>
          </div>

          {result.valid && (
            <div className="mt-6 grid md:grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white border border-green-200 p-4">
                <div className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">IBAN</div>
                <div className="font-mono-fin text-slate-900 mt-1 tracking-wider">{groupIban(result.iban)}</div>
              </div>
              <div className="rounded-2xl bg-white border border-green-200 p-4">
                <div className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">País</div>
                <div className="text-slate-900 mt-1">{result.country_flag} {result.country_name} <span className="font-mono-fin text-slate-400 text-xs">({result.country})</span></div>
              </div>
              <div className="rounded-2xl bg-white border border-green-200 p-4">
                <div className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">Código entidad</div>
                <div className="font-mono-fin text-slate-900 mt-1">{result.bank_code}</div>
              </div>
              <div className="rounded-2xl bg-white border border-green-200 p-4 flex items-start gap-3">
                <Building2 size={18} className="text-blue-600 mt-0.5" />
                <div>
                  <div className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">Banco</div>
                  <div className="text-slate-900 mt-1 font-semibold">{result.bank_name}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-slate-900 text-white p-6 rounded-3xl">
        <div className="flex items-center gap-2 text-blue-300 font-mono-fin text-xs uppercase tracking-widest">
          <Hash size={14} /> Algoritmo ISO 13616
        </div>
        <p className="text-slate-300 mt-3 text-sm leading-relaxed">
          1) Comprobamos las 2 letras de país y los 2 dígitos de control. <br/>
          2) Validamos la longitud por país (España 24, Alemania 22, etc.). <br/>
          3) Movemos los 4 primeros al final y convertimos letras a números (A=10... Z=35). <br/>
          4) El IBAN es válido si <span className="font-mono-fin text-white">mod 97 = 1</span>. <br/>
          5) Detectamos el código de entidad para identificar el banco emisor.
        </p>
      </div>
    </div>
  );
}
